import { RuleDataFieldResource } from "./dataField";

export enum RuleTypes {
	IS_REQUIRED = "isRequired",
	IS_EMPTY = "isEmpty",
	IS_NOT_EMPTY = "isNotEmpty",
	NOT_UPDATABLE = "notUpdatable",
	READ_ONLY = "readOnly",
	HAS_VALUE = "hasValue",
	HAS_NOT_VALUE = "hasNotValue",
	HAS_MAX_CHARS = "hasMaxChars",
	HAS_MIN_CHARS = "hasMinChars",
	MUST_CONTAIN = "mustContain",
	MUST_NOT_CONTAIN = "mustNotContain",
	LESS_THAN_EQUAL = "lessThanEqual",
	GREATER_THAN_EQUAL = "greaterThanEqual",
	CONTAINS_ONE = "containsOne",
	NOT_CONTAINS_RULE = "notContainsOne",
	REGEX = "regex",
	BETWEEN = "between",
}

export type RelationRuleGuestAttributeEntityType =
	| "wave"
	| "groups"
	| "tenant"
	| "tags"
	| "guestType"
	| "region"
	| "hotelStay.checkIn"
	| "hotelStay.checkOut";

export interface RuleGuestAttributeResourceTypeRelation {
	id: string;
	type: "relation";
	entityType: RelationRuleGuestAttributeEntityType;
}

export interface RuleGuestAttributeResourceTypeDataField {
	id: string;
	type: "dataField";
	dataField: RuleDataFieldResource;
}

export type RuleGuestAttributeResource =
	| RuleGuestAttributeResourceTypeRelation
	| RuleGuestAttributeResourceTypeDataField;

interface BaseRule<TAttribute extends RuleGuestAttributeResource> {
	id: number;
	sectionId: number | null;
	guestAttribute: TAttribute;
	guestAttributeReference: RuleGuestAttributeResourceTypeDataField | null;
}

export interface RuleResourceTypeIsRequired<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: null;
	type: RuleTypes.IS_REQUIRED;
}

export interface RuleResourceTypeIsEmpty<TAttribute extends RuleGuestAttributeResource> extends BaseRule<TAttribute> {
	value: null;
	type: RuleTypes.IS_EMPTY;
}

export interface RuleResourceTypeIsNotEmpty<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: null;
	type: RuleTypes.IS_NOT_EMPTY;
}

export interface RuleResourceTypeNotUpdatable<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: null;
	type: RuleTypes.NOT_UPDATABLE;
}

export interface RuleResourceTypeReadOnly<TAttribute extends RuleGuestAttributeResource> extends BaseRule<TAttribute> {
	value: null;
	type: RuleTypes.READ_ONLY;
}

export interface RuleResourceTypeHasValue<TAttribute extends RuleGuestAttributeResource> extends BaseRule<TAttribute> {
	value: number | string | number[] | boolean;
	type: RuleTypes.HAS_VALUE;
}

export interface RuleResourceTypeHasNotValue<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: number | string | string[] | number[] | boolean;
	type: RuleTypes.HAS_NOT_VALUE;
}

export interface RuleResourceTypeHasMaxChars<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: number;
	type: RuleTypes.HAS_MAX_CHARS;
}

export interface RuleResourceTypeHasMinChars<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: number;
	type: RuleTypes.HAS_MIN_CHARS;
}

export interface RuleResourceTypeMustContain<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: number | string | number[] | boolean;
	type: RuleTypes.MUST_CONTAIN;
}

export interface RuleResourceTypeMustNotContain<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: number | string | number[] | boolean;
	type: RuleTypes.MUST_NOT_CONTAIN;
}

export interface RuleResourceTypeLessThanEqual<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: number | number[] | string | string[] | null;
	type: RuleTypes.LESS_THAN_EQUAL;
}

export interface RuleResourceTypeGreaterThanEqual<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: number | string | null;
	type: RuleTypes.GREATER_THAN_EQUAL;
}

export interface RuleResourceTypeContainsOne<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: string[] | number[];
	type: RuleTypes.CONTAINS_ONE;
}

export interface RuleResourceTypeNotContainsOne<
	TAttribute extends RuleGuestAttributeResource,
> extends BaseRule<TAttribute> {
	value: string[] | number[];
	type: RuleTypes.NOT_CONTAINS_RULE;
}

export interface RuleResourceTypeRegex<TAttribute extends RuleGuestAttributeResource> extends BaseRule<TAttribute> {
	value: string;
	type: RuleTypes.REGEX;
}

export interface RuleResourceTypeBetween<TAttribute extends RuleGuestAttributeResource> extends BaseRule<TAttribute> {
	value: { start: string; end: string } | null;
	type: RuleTypes.BETWEEN;
}

export type RuleResource<TAttribute extends RuleGuestAttributeResource> =
	| RuleResourceTypeIsRequired<TAttribute>
	| RuleResourceTypeIsEmpty<TAttribute>
	| RuleResourceTypeIsNotEmpty<TAttribute>
	| RuleResourceTypeNotUpdatable<TAttribute>
	| RuleResourceTypeReadOnly<TAttribute>
	| RuleResourceTypeHasValue<TAttribute>
	| RuleResourceTypeHasNotValue<TAttribute>
	| RuleResourceTypeHasMaxChars<TAttribute>
	| RuleResourceTypeHasMinChars<TAttribute>
	| RuleResourceTypeMustContain<TAttribute>
	| RuleResourceTypeMustNotContain<TAttribute>
	| RuleResourceTypeLessThanEqual<TAttribute>
	| RuleResourceTypeGreaterThanEqual<TAttribute>
	| RuleResourceTypeContainsOne<TAttribute>
	| RuleResourceTypeNotContainsOne<TAttribute>
	| RuleResourceTypeRegex<TAttribute>
	| RuleResourceTypeBetween<TAttribute>;
